import { useState, useEffect, useCallback } from 'react';
import { customerService } from '../services/customerService';
import { useAuth } from '../contexts/AuthContext';

export const useCustomersPaginated = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  
  // Filtros e configurações
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageSize, setPageSize] = useState(20);
  
  const { user } = useAuth();

  // Resetar paginação quando filtros mudarem
  const resetPagination = useCallback(() => {
    setCustomers([]);
    setLastDoc(null);
    setHasMore(false);
    setTotalCount(0);
  }, []);

  // Carregar primeira página
  const loadFirstPage = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setLoading(true);
      setError(null);
      resetPagination();

      const result = await customerService.getByCompanyPaginated(user.companyId, {
        limit: pageSize,
        searchTerm: searchTerm.trim(),
        sortField,
        sortDirection
      });

      setCustomers(result.customers);
      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);
      setLastDoc(result.lastDoc);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar primeira página:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, pageSize, searchTerm, sortField, sortDirection, resetPagination]);

  // Carregar próxima página
  const loadNextPage = useCallback(async () => {
    if (!user?.companyId || !hasMore || !lastDoc || loading) return;

    try {
      setLoading(true);
      setError(null);

      const result = await customerService.getByCompanyPaginated(user.companyId, {
        limit: pageSize,
        startAfter: lastDoc,
        searchTerm: searchTerm.trim(),
        sortField,
        sortDirection
      });

      setCustomers(prev => [...prev, ...result.customers]);
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar próxima página:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, hasMore, lastDoc, loading, pageSize, searchTerm, sortField, sortDirection]);

  // Atualizar busca (debounced)
  const updateSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  // Atualizar ordenação
  const updateSort = useCallback((field, direction = 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  // Atualizar tamanho da página
  const updatePageSize = useCallback((size) => {
    setPageSize(size);
  }, []);

  // Adicionar cliente
  const addCustomer = async (customerData) => {
    try {
      if (!user?.companyId) {
        throw new Error('Usuário não está associado a uma empresa');
      }

      const newCustomer = await customerService.create({
        name: customerData.name,
        cpf: customerData.cpf,
        phone: customerData.phone,
        email: customerData.email,
        emails: customerData.emails, // Incluir array de múltiplos e-mails
        birthDate: customerData.birthDate,
        referralCode: customerData.referralCode, // Código de quem indicou este cliente
        companyId: user.companyId,
        // Incluir campos de endereço se existirem
        cep: customerData.cep,
        street: customerData.street,
        number: customerData.number,
        complement: customerData.complement,
        city: customerData.city,
        state: customerData.state,
        neighborhood: customerData.neighborhood
      });
      
      // Recarregar primeira página para mostrar o novo cliente
      await loadFirstPage();
      return newCustomer;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao adicionar cliente:', err);
      throw err;
    }
  };

  // Atualizar cliente
  const updateCustomer = async (customerId, customerData) => {
    try {
      const updatedCustomer = await customerService.update(customerId, customerData);
      
      // Atualizar cliente na lista local
      setCustomers(prev => 
        prev.map(customer => 
          customer.id === customerId ? { ...customer, ...updatedCustomer } : customer
        )
      );
      return updatedCustomer;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao atualizar cliente:', err);
      throw err;
    }
  };

  // Excluir cliente
  const deleteCustomer = async (customerId) => {
    try {
      await customerService.delete(customerId);
      
      // Remover cliente da lista local
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
      setTotalCount(prev => prev - 1);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao excluir cliente:', err);
      throw err;
    }
  };

  // Carregar primeira página quando dependências mudarem
  useEffect(() => {
    if (user?.companyId) {
      const timeoutId = setTimeout(() => {
        loadFirstPage();
      }, 300); // Debounce de 300ms

      return () => clearTimeout(timeoutId);
    }
  }, [user?.companyId, loadFirstPage]);

  return {
    // Dados
    customers,
    loading,
    error,
    hasMore,
    totalCount,
    
    // Filtros
    searchTerm,
    sortField,
    sortDirection,
    pageSize,
    
    // Ações
    loadFirstPage,
    loadNextPage,
    updateSearch,
    updateSort,
    updatePageSize,
    addCustomer,
    updateCustomer,
    deleteCustomer
  };
};
